import React, { useState, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import styled from 'styled-components';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import backgroundImage from '../images/10246984.jpeg';
import logo from '../images/logo192.png';

const SERVER_URL = 'https://nuedesigns.com/';

// Optimized image placeholders
const PLACEHOLDERS = {
  apartment1: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltN1n2V1o3W9tW+1l8uP//Z',
  apartment2: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAAIAAoDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltN1n2V1o3W9tW+1l8uP//Z',
  office: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQABAQAAAQABAAD/2wBDAAYEBQYFBAYGBQYHBwYIChAKCgkJChQODwwQFxQYGBcUFhYaHSUfGhsjHBYWICwgIyYnKSopGR8tMC0oMCUoKSj/2wBDAQcHBwoIChMKChMoGhYaKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCgoKCj/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAv/xAAhEAACAQMDBQAAAAAAAAAAAAABAgMABAUGIWGRkqGx0f/EABUBAQEAAAAAAAAAAAAAAAAAAAMF/8QAGhEAAgIDAAAAAAAAAAAAAAAAAAECEgMRkf/aAAwDAQACEQMRAD8AltN1n2V1o3W9tW+1l8uP//Z'
};

const FullPage = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const MagazineHeader = styled.div`
  position: relative;
  z-index: 10;
  text-align: center;
padding: 2.5rem 2rem 2rem;
  background: linear-gradient(135deg, rgba(10, 25, 47, 0.85) 0%, rgba(26, 54, 93, 0.85) 100%);

  @media (max-width: 768px) {
    padding: 1.5rem 1.5rem 1.5rem;
  }
`;

const HeaderTitle = styled.h1`
  font-family: "Raleway-Regular";
  font-size: clamp(2rem, 6vw, 2.5rem);
  font-weight: 600;
  letter-spacing: 0.08em;
  color: #f1dec8;
  margin: 0 0 0.5rem 0;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
`;

const HeaderSubtitle = styled.p`
  font-size: clamp(0.9rem, 2vw, 1.1rem);
  color: #e1ad21;
  margin: 0;
  letter-spacing: 0.05em;
  font-weight: 500;
`;

const GalleryContainer = styled.div`
  background-image: url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #0a192f;
  flex: 1;
  padding: 6rem 4rem 4rem;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: 1024px) {
    padding: 4rem 2rem 2rem;
  }

  @media (max-width: 768px) {
    padding: 3rem 1.2rem 1.5rem;
    background-attachment: scroll; /* avoids jank on mobile */
  }
`;
const ProjectContainer = styled.div`
  max-width: 900px;
  width: 100%;
  margin: 0 auto;
  position: relative;
`;

const MagazinePage = styled.div`
  background: #fdf1e5;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 40px 80px rgba(0, 0, 0, 0.4), 0 15px 35px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  padding: 2.5rem;
  padding-bottom: 4rem;
  position: relative;
  will-change: transform;
  transition: box-shadow 0.3s ease;

  &:hover {
    box-shadow: 0 50px 100px rgba(0, 0, 0, 0.5), 0 20px 40px rgba(0, 0, 0, 0.25);
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1.2rem 2.5rem;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
  }
`;

const ProjectTitle = styled.h2`
  font-family: "Raleway-Regular", sans-serif;
  font-size: clamp(1.6rem, 4vw, 2rem);
  font-weight: 700;
  color: #0a192f;
  margin: 0 0 0.5rem 0;
  letter-spacing: 0.02em;
`;

const ProjectType = styled.p`
  font-size: 1rem;
  color: #e1ad21;
  margin: 0 0 1.5rem 0;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.2rem;
  margin-bottom: 2rem;
`;

const ImageItem = styled.a`
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
  transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
  text-decoration: none;
  cursor: pointer;
  height: 180px;
  grid-column: ${({ $full }) => $full ? '1 / -1' : 'auto'};
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.25);

    img {
      transform: scale(1.08);
    }

    ${({ $full }) => $full && `
      .overlay-button {
        opacity: 1;
      }
    `}
  }
`;

const Image = styled(LazyLoadImage)`
  width: 100%;
  height: 100%;
  object-fit: cover;
  will-change: transform;
  transition: transform 0.35s ease;
`;

const OverlayButton = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  border-radius: 8px;
  z-index: 10;
`;

const OverlayButtonText = styled.span`
  background: linear-gradient(135deg, #e1ad21, #ffed4a);
  color: #0a192f;
  padding: 0.8rem 2rem;
  border-radius: 50px;
  font-weight: 700;
  font-size: 0.9rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  box-shadow: 0 8px 20px rgba(255, 215, 0, 0.3);
`;

const ProjectDescription = styled.div`
  border-top: 2px solid #e1ad21;
  padding-top: 1.5rem;
  margin-bottom: 2rem;
`;

const DescriptionTitle = styled.h3`
  font-size: 0.95rem;
  font-weight: 700;
  color: #0a192f;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.8rem 0;
`;

const DescriptionText = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: #444;
  margin: 0;
`;

const NavigationContainer = styled.div`
  display: flex;
  gap: 1.2rem;
  justify-content: center;
  margin-left: -25px;
  margin-top: auto;

  @media (max-width: 768px) {
    margin-left: 0;
    gap: 0.8rem;
    flex-wrap: wrap;
  }
`;

const NavButton = styled.button`
  background: linear-gradient(135deg, #e1ad21, #ffed4a);
  border: none;
  color: #0a192f;
  padding: 0.9rem 1.8rem;
  border-radius: 50px;
  cursor: pointer;
  font-weight: 700;
  font-size: 0.95rem;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  box-shadow: 0 10px 25px rgba(255, 215, 0, 0.3);
  display: flex;
  align-items: center;
  gap: 0.6rem;
  letter-spacing: 0.03em;
  text-transform: uppercase;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #ffed4a, #e1ad21);
    transform: translateY(-4px);
    box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1.4rem;
    font-size: 0.85rem;
  }
`;

const SpreadTitle = styled.div`
  position: absolute;
  font-family: "Helvetica";
  top: -2rem;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #e1ad21, #ffed4a);
  color: #0a192f;
  padding: 0.8rem 2rem;
  border-radius: 50px;
  box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  z-index: 2;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    padding: 0.6rem 1.5rem;
    top: -1.7rem;
  }
`;

const Logo = styled.img`
  position: fixed;
  bottom: 1.5rem;
  right: 2rem;
  width: 40px;
  height: 40px;
  opacity: 0.8;
  pointer-events: none;
  z-index: 5;

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
    bottom: 1rem;
    right: 1rem;
  }
`;

const PageIndicator = styled.div`
  position: fixed;
  top: 50%;
  right: 2rem;
  transform: translateY(-50%);
  text-align: center;
  z-index: 5;

  @media (max-width: 1024px) {
    position: static;
    transform: none;
    margin-top: 2rem;
    right: auto;
  }

  @media (max-width: 768px) {
    margin-top: 1.5rem;
  }
`;

const PageNumber = styled.div`
  color: #ffd700;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const PageTotal = styled.div`
  color: #fff;
  font-size: 0.9rem;
  letter-spacing: 0.05em;
`;

const PROJECT_DESCRIPTIONS = {
  'bachelor-pad': 'A sleek and sophisticated 1-bedroom apartment designed for the modern professional. This intimate space maximizes comfort and style with premium finishes, intelligent space planning, and curated design elements that create a refined sanctuary.',
  'contemporary-family-home': 'An elegant 3-bedroom residence combining contemporary aesthetics with family-focused design. Open-plan living spaces, premium finishes, and thoughtful layouts create the perfect environment for modern family living with sophisticated style.',
  'corporate-office-space': 'A state-of-the-art commercial workspace designed to inspire creativity and collaboration. Flexible layouts, cutting-edge technology integration, and premium finishes create an inspiring atmosphere for productive work.',
  'modern-two-bedroom': 'A contemporary 2-bedroom apartment featuring smart space utilization and modern design. Perfect for young professionals or small families seeking a balance between comfort and contemporary living.',
  'luxury-three-bedroom': 'An upscale 3-bedroom residence with luxury finishes throughout. This spacious home offers premium materials, intelligent design, and elegant aesthetics for discerning residents.'
};

function Gallery() {
  const [currentProject, setCurrentProject] = useState(0);
  const containerRef = useRef(null);

  const projects = [
    {
      id: 1,
      slug: 'projects/bachelor-pad',
      name: 'Bachelor Pad',
      type: 'Residential • 1 Bed',
      images: [
        { url: `${SERVER_URL}/assets/images/Apartment1Bed.jpg`, placeholder: PLACEHOLDERS.apartment1 },
        { url: `${SERVER_URL}/assets/images/BachelorPad2.jpg`, placeholder: PLACEHOLDERS.apartment1 },
        { url: `${SERVER_URL}/assets/images/BachelorPad6.jpg`, placeholder: PLACEHOLDERS.apartment1 }
      ]
    },
    {
      id: 2,
      slug: 'projects/contemporary-family-home',
      name: 'Contemporary Family Home',
      type: 'Residential • 3 Beds',
      images: [
        { url: `${SERVER_URL}/assets/images/Contemporary1.jpg`, placeholder: PLACEHOLDERS.apartment2 },
        { url: `${SERVER_URL}/assets/images/Contemporary2.jpg`, placeholder: PLACEHOLDERS.apartment2 },
        { url: `${SERVER_URL}/assets/images/Contemporary4.jpg`, placeholder: PLACEHOLDERS.apartment2 }
      ]
    },
    {
      id: 3,
      slug: 'projects/corporate-office-space',
      name: 'Corporate Office Space',
      type: 'Commercial',
      images: [
        { url: `${SERVER_URL}/assets/images/Office1.jpg`, placeholder: PLACEHOLDERS.office },
        { url: `${SERVER_URL}/assets/images/Office2.jpg`, placeholder: PLACEHOLDERS.office },
        { url: `${SERVER_URL}/assets/images/Office3.jpg`, placeholder: PLACEHOLDERS.office }
      ]
    },
    {
      id: 4,
      slug: 'projects/modern-two-bedroom',
      name: 'Modern Two Bedroom',
      type: 'Residential • 2 Beds',
      images: [
        { url: `${SERVER_URL}/assets/images/Placeholderbanner.jpg`, placeholder: PLACEHOLDERS.apartment1 },
        { url: `${SERVER_URL}/assets/images/Placeholderbanner.jpg`, placeholder: PLACEHOLDERS.apartment1 },
        { url: `${SERVER_URL}/assets/images/Placeholderbanner.jpg`, placeholder: PLACEHOLDERS.apartment1 }
      ]
    },
    {
      id: 5,
      slug: 'projects/luxury-three-bedroom',
      name: 'Luxury Three Bedroom',
      type: 'Residential • 3 Beds',
      images: [
        { url: `${SERVER_URL}/assets/images/Placeholderbanner.jpg`, placeholder: PLACEHOLDERS.apartment2 },
        { url: `${SERVER_URL}/assets/images/Placeholderbanner.jpg`, placeholder: PLACEHOLDERS.apartment2 },
        { url: `${SERVER_URL}/assets/images/Placeholderbanner.jpg`, placeholder: PLACEHOLDERS.apartment2 }
      ]
    }
  ];

  const totalProjects = projects.length;
  const canPrev = currentProject > 0;
  const canNext = currentProject < totalProjects - 1;

  const handlePrev = useCallback(() => {
    if (canPrev) {
      setCurrentProject(prev => prev - 1);
      setTimeout(() => {
        if (containerRef.current) containerRef.current.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [canPrev]);

  const handleNext = useCallback(() => {
    if (canNext) {
      setCurrentProject(prev => prev + 1);
    }
  }, [canNext]);

  const project = projects[currentProject];

  return (
    <FullPage>
      <MagazineHeader>
        <HeaderTitle>Curated Design & Architecture Projects</HeaderTitle>
      </MagazineHeader>

      <GalleryContainer ref={containerRef}>
        <ProjectContainer>
          <MagazinePage>
            <ProjectTitle>{project.name}</ProjectTitle>
            <ProjectType>{project.type}</ProjectType>

            <ImageGrid>
              {project.images[0] && (
                <ImageItem href={`/${project.slug}`} $full>
                  <Image
                    src={project.images[0].url}
                    placeholderSrc={project.images[0].placeholder}
                    alt={project.name}
                    effect="blur"
                    draggable={false}
                  />
                  <OverlayButton className="overlay-button">
                    <OverlayButtonText>View Full Project</OverlayButtonText>
                  </OverlayButton>
                </ImageItem>
              )}
              {project.images[1] && (
                <ImageItem href={`/${project.slug}`}>
                  <Image
                    src={project.images[1].url}
                    placeholderSrc={project.images[1].placeholder}
                    alt={project.name}
                    effect="blur"
                    draggable={false}
                  />
                </ImageItem>
              )}
              {project.images[2] && (
                <ImageItem href={`/${project.slug}`}>
                  <Image
                    src={project.images[2].url}
                    placeholderSrc={project.images[2].placeholder}
                    alt={project.name}
                    effect="blur"
                    draggable={false}
                  />
                </ImageItem>
              )}
            </ImageGrid>

            <ProjectDescription>
              <DescriptionTitle>About This Project</DescriptionTitle>
              <DescriptionText>
                {PROJECT_DESCRIPTIONS[project.slug.split('/')[1]] || project.name}
              </DescriptionText>
            </ProjectDescription>

            <NavigationContainer>
              <NavButton onClick={handlePrev} disabled={!canPrev}>
                <ChevronLeft size={20} />
                Previous
              </NavButton>
              <NavButton onClick={handleNext} disabled={!canNext}>
                Next
                <ChevronRight size={20} />
              </NavButton>
            </NavigationContainer>
          </MagazinePage>

          <SpreadTitle>Project {currentProject + 1} of {totalProjects}</SpreadTitle>
        </ProjectContainer>
      </GalleryContainer>

      <PageIndicator>
        <PageNumber>{currentProject + 1}</PageNumber>
        <PageTotal>of {totalProjects}</PageTotal>
      </PageIndicator>

      <Logo src={logo} alt="Logo" />
    </FullPage>
  );
}

export default React.memo(Gallery);
